"use client";

import { Check, ChevronRight, FileText } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useId, useRef, useState } from "react";
import { QUIET_HOVER } from "@/components/creatorPortal/one/chrome";
import { Input } from "@/components/ui/design-system/input";
import { SURFACE } from "@/components/ui/design-system/surface-styles";
import { Hint, Strong } from "@/components/ui/design-system/text";
import { useIsWide } from "@/components/ui/design-system/use-is-wide";
import { cn } from "@/lib/utils";
import { AgreementSheet } from "./AgreementSheet";
import { AgreementText } from "./AgreementText";
import { MAX_SHORT_TEXT_LENGTH, namesMatch } from "./applyForm";
import { Field } from "./applyFormPrimitives";

/**
 * The agreement and the signature sit on one step, in that order, so the
 * typed signature is directly under the text it signs — the whole basis for
 * treating this as an electronic signature is that the signer was shown the
 * terms at the moment they signed.
 *
 * One step for both places a creator signs: the /join application, and the
 * portal when a revised agreement needs an existing creator's signature.
 * `signerName` is the name the signature must match where there is one: the
 * name typed earlier in the same application. The portal has none (the
 * profile name was typed by staff), so there the hint just asks for a name.
 */
export function AgreementSigningStep({
  agreementText,
  agreed,
  agreedError,
  onAgreedChange,
  read,
  onRead,
  signerName,
  signatureName,
  signatureError,
  onSignatureNameChange,
}: {
  agreementText: string;
  agreed: boolean;
  agreedError?: string;
  onAgreedChange: (agreed: boolean) => void;
  /** Whether this text has been read to the end. Owned by the flow, so it
   *  survives leaving the step and coming back. */
  read: boolean;
  onRead: () => void;
  signerName?: string;
  signatureName: string;
  signatureError?: string;
  onSignatureNameChange: (value: string) => void;
}) {
  const t = useTranslations("agreement");
  const locale = useLocale();
  const agreedErrorId = useId();
  // The signature only means anything if the terms were actually put in front
  // of the signer, and a 320px window over a multi-page agreement is easy to
  // sign past. The box has to be scrolled to the end before the checkbox
  // unlocks. Short viewports where the text already fits count as read.
  const scrollRef = useRef<HTMLElement>(null);
  // A phone reads it in a sheet; a desk reads it in place. The same rule
  // either way: the end has to be reached before the box unlocks.
  const wide = useIsWide();
  const [reading, setReading] = useState(false);

  // Two different facts. `read` latches: once the end has been
  // reached the box stays unlocked. `atBottom` is only where the reader is
  // right now, and drives the fade, which should be there whenever there is
  // more text below, including after scrolling back up a finished agreement.
  const [atBottom, setAtBottom] = useState(false);
  const markIfAtEnd = (el: HTMLElement) => {
    // 8px of slack: sub-pixel line heights and zoom leave a residue that
    // never quite reaches an exact equality.
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
    setAtBottom(bottom);
    if (bottom && !read) onRead();
  };

  // Back to the top whenever the text is new to this box, and measured:
  // an agreement short enough to fit has nothing to scroll, and gating on a
  // scroll that can never happen would make the form impossible to submit.
  // Whether it counts as read is the flow's call, keyed to the text itself,
  // so a republished version arrives unread without anything here resetting.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = 0;
      markIfAtEnd(el);
    }
  }, [agreementText]);

  // The inline box only exists at desk width, which is known after hydration,
  // so it can mount after the effect above has run. Measure it when it does:
  // an agreement short enough to fit still has to count as read.
  useEffect(() => {
    if (scrollRef.current) markIfAtEnd(scrollRef.current);
  }, [wide]);

  // One pane holding the text and the promise to it: they are one thing, so
  // they share a surface instead of being two ringed boxes stacked on the
  // ground. The DS card recipe, fill and radius, no line; anything inside
  // separates by space and by the hover wash, never by a rule.
  const agreeRow = (
    <label
      className={cn(
        "flex w-full items-start gap-nf-ds-3 px-nf-ds-6 py-nf-ds-5",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-nf-ds-brand",
        read
          ? cn(
              "cursor-pointer select-none transition-[background-color] duration-nf-ds-1 ease-nf-ds-out",
              QUIET_HOVER,
            )
          : "cursor-not-allowed",
      )}
    >
      <input
        type="checkbox"
        checked={agreed}
        disabled={!read}
        aria-describedby={agreedError ? agreedErrorId : undefined}
        onChange={(e) => onAgreedChange(e.target.checked)}
        className="mt-nf-ds-1 size-nf-ds-4 shrink-0 cursor-pointer accent-nf-ds-brand outline-hidden disabled:cursor-not-allowed disabled:opacity-40"
      />
      <span
        className={cn(
          "font-nf-ds-sans text-nf-ds-body",
          read ? "text-nf-ds-fg" : "text-nf-ds-fg-muted",
        )}
      >
        {t("checkbox")}
      </span>
    </label>
  );

  return (
    <div>
      <div className={cn(SURFACE, "overflow-hidden")}>
        {wide ? (
          <>
            {/* Scrolls inside the pane so the agreement can never push the
                signature off the bottom of the screen. */}
            <section
              ref={scrollRef}
              onScroll={(event) => markIfAtEnd(event.currentTarget)}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region has to be focusable to be scrollable from the keyboard at all
              tabIndex={0}
              aria-label={t("name")}
              className="nf-scroll max-h-96 overflow-y-auto outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nf-ds-brand"
            >
              <div className="px-nf-ds-6 pt-nf-ds-6 pb-nf-ds-4">
                {/* The agreement itself is counsel-approved English,
                    translated only with counsel's sign-off, so a reader in
                    another language is told before they start. */}
                {locale !== "en" ? (
                  <Hint muted as="p" className="mb-nf-ds-4">
                    {t("englishOnly")}
                  </Hint>
                ) : null}
                <AgreementText text={agreementText} />
              </div>
              {/* A scroll edge rather than a rule: the text fades into the
                  bottom while there is more of it, which says "keep going"
                  without a word, and clears once the end is reached. Sticky
                  with a matching negative margin so it overlays the text
                  instead of adding a strip of empty pane under it. */}
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none sticky bottom-0 -mt-nf-ds-12 h-nf-ds-12 bg-linear-to-t from-nf-ds-surface-card to-transparent",
                  "transition-opacity duration-nf-ds-2 ease-nf-ds-out",
                  atBottom && "opacity-0",
                )}
              />
            </section>
            {/* On the agreement, where the text ends: the state it describes
                is the text's, and it sits right above the box it unlocks. */}
            {!read ? (
              <Hint muted as="p" className="px-nf-ds-6 pt-nf-ds-4">
                {t("readingHint")}
              </Hint>
            ) : null}
          </>
        ) : (
          <>
            {/* The door to the sheet. It carries the state too, so coming
                back to this step says whether the reading is done without
                opening anything. */}
            <button
              type="button"
              onClick={() => setReading(true)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-nf-ds-3 px-nf-ds-6 pt-nf-ds-5 pb-nf-ds-3 text-left",
                "outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nf-ds-brand",
                "transition-[background-color] duration-nf-ds-1 ease-nf-ds-out",
                QUIET_HOVER,
              )}
            >
              <FileText
                aria-hidden="true"
                className="size-nf-ds-5 shrink-0 text-nf-ds-fg-muted"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-nf-ds-1">
                <Strong>{t("name")}</Strong>
                <Hint muted>{read ? t("door.read") : t("door.unread")}</Hint>
              </span>
              {read ? (
                <Check
                  aria-hidden="true"
                  className="size-nf-ds-5 shrink-0 text-nf-ds-success"
                />
              ) : (
                <ChevronRight
                  aria-hidden="true"
                  className="size-nf-ds-5 shrink-0 text-nf-ds-fg-muted"
                />
              )}
            </button>
            <AgreementSheet
              open={reading}
              onClose={() => setReading(false)}
              text={agreementText}
              reachedEnd={read}
              onReachedEnd={onRead}
            />
          </>
        )}
        {agreeRow}
      </div>
      {agreedError ? (
        <p
          id={agreedErrorId}
          role="alert"
          className="mt-nf-ds-2 font-nf-ds-sans text-nf-ds-hint text-nf-ds-destructive"
        >
          {agreedError}
        </p>
      ) : null}

      <div className="mt-nf-ds-8">
        <Field
          label={t("signature.label")}
          // Says what to type rather than what not to: the name is right
          // there from step 1, and making someone scroll back to find the
          // spelling they used is a test nobody asked for. Once it matches,
          // the hint confirms it, so the enabled button has a reason.
          hint={
            signerName === undefined
              ? t("signature.hintLegalName")
              : namesMatch(signatureName, signerName)
                ? t("signature.signedAs", { name: signerName.trim() })
                : t("signature.hint", { name: signerName.trim() })
          }
          error={signatureError}
        >
          <Input
            value={signatureName}
            onChange={(e) => onSignatureNameChange(e.target.value)}
            maxLength={MAX_SHORT_TEXT_LENGTH}
            placeholder={t("signature.placeholder")}
            className="italic"
          />
        </Field>
      </div>
    </div>
  );
}
